import { Injectable } from '@angular/core';
import { UserService } from '../../user.service';
import { markLenientSource } from '../../../shared/helpers/lenient-template-source';
import { BaseTemplateMapper, EmailTemplate, EntityFieldSource, FieldSourceKind, FilterOption, FilterValue, TemplateMapperConfig } from '../base-template-mapper';
import { TemplateContext, ReportTemplate } from '../../../models/reportTemplate.model';
import { RiskRegister, RiskAssessment, RiskLevel, LikelihoodColorLevel } from '../../../models/risk-assessment.model';
import { StatisticsEnum, TableStatisticsEnum } from '../../../enums/assessmentStatus.enum';
import { DateFormatService } from '../../dateFormat.service';
import { TranslateService } from '../../translate.service';
import { ENTITY_TYPES } from 'src/app/shared/widget-registry';
import { getEnumList } from '../../../_helpers/enum-helper';
import { getRegisterStatisticValue } from 'src/app/shared/utils/risk-statistic-value.util';
import {
    RiskRegisterMatrixData,
    RiskRegisterMatrixRow,
    RiskRegisterMatrixColumn,
    RiskRegisterMatrixCell,
    RiskRegisterMatrixGroup,
    RiskRegisterStatistic,
    RiskRegisterTablePresentationService
} from '../../../components/template-widgets/risk-widgets/risk-register-table-widget/risk-register-table-presentation.service';
import { SegProfilePresentationService } from '../../../components/template-widgets/risk-widgets/seg-profile/seg-profile-presentation.service';
import { RiskRegisterSegGroup } from './risk-register-seg-group.model';
import { RiskRegisterDisplayRow } from '../../../models/risk-register-grouping.model';
import { selectGroupedEntries } from '../../../components/risk-register/risk-register-grouping.util';

export interface RiskRegisterReportAgent {
    agentId: string;
    agentName: string;
    consequenceRating: number | string | null;
}

export interface RiskRegisterReportEntity {
    dataList: RiskRegister[];
    groupedRows?: RiskRegisterDisplayRow[];
    agents: RiskRegisterReportAgent[];
    riskLevels: RiskLevel[];
    riskAssessment: RiskAssessment;
    qualitativeExceedanceLabel?: string;
    qualitativeFrequencyLabel?: string;
    exposureLikelihoodLevels?: LikelihoodColorLevel[];
    selectedSegIds?: string[];
    selectedAgentIds?: string[];
    selectedStatisticKeys?: string[];
}

interface RiskRegisterFilteredData {
    rows: RiskRegister[];
    agentIds: string[];
    statisticKeys: string[];
}

const FILTER_KEY_SEG = 'segId';
const FILTER_KEY_AGENT = 'agentId';
const FILTER_KEY_STATISTIC = 'statistic';

// Widget tags that mark a template as SEG-shaped (the SEG Profile report), as opposed to the
// shipped matrix 'Risk Register Report'. The Statistics filter selects which statistic *columns*
// render in the matrix; the SEG per-agent table has a fixed, curated column set with no statistic-
// column surface, so the filter would render but do nothing there. It is therefore dropped from the
// panel for SEG-shaped templates (DASH-2744): a filter that renders but does nothing is worse than
// no filter. Detection matches how report-template-editor probes widget presence: `<tag` in layoutHtml.
const SEG_WIDGET_TAGS = [
    'app-seg-profile-header-widget',
    'app-seg-job-roles-widget',
    'app-seg-agents-widget',
    'app-seg-controls-widget',
    'app-seg-tasks-widget'
];

@Injectable({ providedIn: 'root' })
export class RiskRegisterTemplateMapper extends BaseTemplateMapper<RiskRegisterReportEntity> {
    readonly config: TemplateMapperConfig = {
        entityType: ENTITY_TYPES.RISK_REGISTER,
        supportedFilters: [FILTER_KEY_SEG, FILTER_KEY_AGENT, FILTER_KEY_STATISTIC]
    };

    constructor(
        private dateFormatService: DateFormatService,
        private translateService: TranslateService,
        private presentationService: RiskRegisterTablePresentationService,
        private segProfilePresentationService: SegProfilePresentationService,
        private userService: UserService
    ) { super(); }

    analyzeFilters(entity: RiskRegisterReportEntity, template?: ReportTemplate): FilterOption[] {
        if (!entity?.dataList?.length) return [];

        const exceedanceLabel = entity.qualitativeExceedanceLabel ?? this.translateService.getDynamicTranslation('Exceedance Probability');
        const frequencyLabel = entity.qualitativeFrequencyLabel ?? this.translateService.getDynamicTranslation('Frequency of Exposure');

        const segValues: FilterValue[] = entity.dataList.map(row => ({
            value: row.segId,
            label: row.segName ?? `SEG ${row.segId}`,
            count: 1
        }));

        const agentValues: FilterValue[] = (entity.agents ?? []).map(agent => ({
            value: agent.agentId,
            label: agent.agentName ?? `Agent ${agent.agentId}`,
            count: 1
        }));

        const statisticValues: FilterValue[] = getEnumList(StatisticsEnum).map(key => ({
            value: key,
            label: key === 'likelihood'
                ? `${this.translateService.getDynamicTranslation('Likelihood')} (${exceedanceLabel} × ${frequencyLabel})`
                : StatisticsEnum[key as keyof typeof StatisticsEnum],
            count: 1
        }));

        const preSelectedSegIds = entity.selectedSegIds ?? [];
        const preSelectedAgentIds = entity.selectedAgentIds ?? [];
        const preSelectedStatKeys = entity.selectedStatisticKeys ?? [];

        const options: FilterOption[] = [
            {
                path: FILTER_KEY_SEG,
                label: this.translateService.getDynamicTranslation('SEGs'),
                availableValues: segValues,
                selectedValues: preSelectedSegIds.length ? preSelectedSegIds : segValues.map(v => v.value)
            },
            {
                path: FILTER_KEY_AGENT,
                label: this.translateService.getDynamicTranslation('Agents'),
                availableValues: agentValues,
                selectedValues: preSelectedAgentIds.length ? preSelectedAgentIds : agentValues.map(v => v.value)
            }
        ];

        // Statistics only narrows matrix statistic columns; it has no surface on the SEG per-agent
        // table, so omit it for SEG-shaped templates rather than render a filter that does nothing.
        if (!this.isSegShapedTemplate(template)) {
            options.push({
                path: FILTER_KEY_STATISTIC,
                label: this.translateService.getDynamicTranslation('Statistics'),
                availableValues: statisticValues,
                selectedValues: preSelectedStatKeys.length ? preSelectedStatKeys : statisticValues.map(v => v.value)
            });
        }

        return options;
    }

    /** Show-all-when-empty agent rule, shared by the matrix columns/cells and the SEG projection. */
    private agentSelected(agentId: string, selected: string[]): boolean {
        return !selected.length || selected.includes(agentId);
    }

    /**
     * A template is SEG-shaped when its layout hosts any SEG Profile widget (vs the matrix widget),
     * OR when it consumes the SEG projection directly. Hand-authored SEG templates (e.g. the demo
     * `SEG Profile Summary`) carry no widget tags — they iterate the projection with a raw
     * `[[#each computedData.segProfiles]]` loop, because each widget's own loopTemplate self-loops
     * over all SEGs and would produce widget-grouped sections instead of one page per SEG. The
     * `computedData.segProfiles` clause is a strict widening: the matrix `Risk Register Report`
     * contains neither the tags nor that string, so it is unaffected.
     */
    private isSegShapedTemplate(template?: ReportTemplate): boolean {
        const layoutHtml = template?.templateJson?.layoutHtml;
        if (!layoutHtml) return false;
        return SEG_WIDGET_TAGS.some(tag => layoutHtml.includes(`<${tag}`))
            || layoutHtml.includes('computedData.segProfiles');
    }

    applyFilters(entity: RiskRegisterReportEntity, filters: Record<string, (number | string)[]>): unknown[] {
        if (!entity?.dataList?.length) return [];

        const selectedSegIds = filters[FILTER_KEY_SEG] as string[] | undefined;
        const agentIds = filters[FILTER_KEY_AGENT] as string[] | undefined;
        const statisticKeys = filters[FILTER_KEY_STATISTIC] as string[] | undefined;

        if (!selectedSegIds?.length) return [];

        const result: RiskRegisterFilteredData = {
            rows: entity.dataList.filter(row => selectedSegIds.includes(row.segId)),
            agentIds: agentIds?.length ? agentIds : (entity.selectedAgentIds ?? []),
            statisticKeys: statisticKeys?.length ? statisticKeys : (entity.selectedStatisticKeys ?? [])
        };
        return [result];
    }

    async mapToTemplateContext(
        entity: RiskRegisterReportEntity,
        _template: ReportTemplate,
        userInputs: Record<string, string | boolean>,
        filteredData?: unknown[]
    ): Promise<TemplateContext> {
        const exceedanceLabel = entity.qualitativeExceedanceLabel ?? this.translateService.getDynamicTranslation('Exceedance Probability');
        const frequencyLabel = entity.qualitativeFrequencyLabel ?? this.translateService.getDynamicTranslation('Frequency of Exposure');

        const applied = filteredData?.[0] as RiskRegisterFilteredData | undefined;
        const filteredRows = applied?.rows ?? entity.dataList;
        const selectedAgentIds = applied?.agentIds ?? entity.selectedAgentIds ?? [];
        const selectedStatisticKeys = applied?.statisticKeys ?? entity.selectedStatisticKeys ?? [];

        const columns: RiskRegisterMatrixColumn[] = (entity.agents ?? [])
            .filter(agent => this.agentSelected(agent.agentId, selectedAgentIds))
            .map(agent => ({
                agentId: agent.agentId,
                agentName: agent.agentName,
                consequenceRating: agent.consequenceRating != null ? String(agent.consequenceRating) : null
            }));

        const exposureLikelihoodLevels = entity.exposureLikelihoodLevels ?? [];
        const notAvailable = this.translateService.getDynamicTranslation('N/A');

        const toMatrixRow = (
            row: RiskRegister,
            numberOfWorkers: number | null,
            numberOfContractors: number | null,
            group: RiskRegisterMatrixGroup | null = null
        ): RiskRegisterMatrixRow => ({
            segId: row.segId,
            segCode: row.segCode ?? null,
            segName: row.segName ?? '',
            numberOfWorkers,
            numberOfContractors,
            cells: ((row.registers ?? []) as (Record<string, unknown> | undefined)[])
                .filter((r): r is Record<string, unknown> => !!r && this.agentSelected(r['agentId'] as string, selectedAgentIds))
                .map(r => this.toMatrixCell(r, exposureLikelihoodLevels, notAvailable)),
            group
        });
        const filteredBySeg = new Map(filteredRows.map(row => [row.segId, row]));
        const rows: RiskRegisterMatrixRow[] = entity.groupedRows
            ? selectGroupedEntries(entity.groupedRows, filteredBySeg).map(({ header, row, source }) =>
                toMatrixRow(source, row.numberOfWorkers, row.numberOfContractors, { key: header.groupKey, label: header.label }))
            : filteredRows.map(row => toMatrixRow(row, row.numberOfWorkers ?? null, row.numberOfContractors ?? null));

        const statistics: RiskRegisterStatistic[] = selectedStatisticKeys.map(key => {
            const value = key === 'likelihood'
                ? `${this.translateService.getDynamicTranslation('Likelihood')} (${exceedanceLabel} × ${frequencyLabel})`
                : StatisticsEnum[key as keyof typeof StatisticsEnum];
            return { key, value, tableKey: TableStatisticsEnum[key as keyof typeof TableStatisticsEnum] };
        });

        const matrix: RiskRegisterMatrixData = {
            rows,
            columns,
            statistics,
            riskLevels: (entity.riskLevels ?? []).map(level => ({ color: level.Color }))
        };

        const riskRegisterMatrix = this.presentationService.build(matrix, {
            segCode: this.translateService.getDynamicTranslation('SEG Code'),
            segName: this.translateService.getDynamicTranslation('SEG Name'),
            workers: this.translateService.getDynamicTranslation('No of Employees'),
            contractors: this.translateService.getDynamicTranslation('No of Contractors'),
            ratingPrefix: this.translateService.getDynamicTranslation('Rating'),
            consequenceRating: this.translateService.getDynamicTranslation('Consequence Rating:'),
            footer: this.translateService.getDynamicTranslation('Report generated using SEGs, Agents, and Statistics')
        });

        // SEG-shaped projection (DASH-2701): one entry per SEG, additive to the existing matrix so
        // 2387's matrix widget/template keep working unchanged. RiskRegister carries segId plus the
        // SEG-optional fields, so it narrows to the SEG-grouped contract with a single cast (no
        // `unknown` hop): every RiskRegisterSegGroup field beyond segId is optional.
        //
        // Thread the agent selection through the SEG projection (DASH-2744): narrow each group's
        // per-agent `registers` by selectedAgentIds before buildProfiles, using the same rule the
        // matrix cells apply at the cell filter above (empty selection = show all). Without this, the
        // Agents filter changed the matrix columns but left every SEG's per-agent table unfiltered.
        const segGroups: RiskRegisterSegGroup[] = (filteredRows as RiskRegisterSegGroup[]).map(group => ({
            ...group,
            registers: (group.registers ?? []).filter(r => !!r && this.agentSelected(r.agentId, selectedAgentIds))
        }));
        const segProfiles = this.segProfilePresentationService.buildProfiles(segGroups, {
            adequate: this.translateService.getDynamicTranslation('Adequate'),
            notAdequate: this.translateService.getDynamicTranslation('Not Adequate'),
            notAvailable: notAvailable,
            exposureLikelihoodLevels
        });

        const userData = await this.loadCachedUserProfile(this.userService);
        const fmt = (ts: number | Date) => {
            const ms = ts instanceof Date ? ts.getTime() : ts;
            return this.dateFormatService.formatTimestampToString(ms, this.dateFormatService.userDateFormat);
        };

        return {
            riskAssessment: entity.riskAssessment,
            computedData: {
                matrix,
                riskRegisterMatrix,
                segProfiles,
                riskLevels: entity.riskLevels ?? [],
                exposureLikelihoodLevels
            },
            userInputs: { ...this.getDefaultUserInputs(), ...userInputs },
            ...this.buildSyntheticValues(userData, this.dateFormatService),
            reportDate: `${this.translateService.getDynamicTranslation('Report Date')}: ${fmt(new Date())}`,
            assessmentName: entity.riskAssessment?.name ?? '',
            assessmentDateRange: `${fmt(entity.riskAssessment?.periodStart)} - ${fmt(entity.riskAssessment?.periodEnd)}`
        };
    }

    private toMatrixCell(
        raw: Record<string, unknown>,
        exposureLikelihoodLevels: LikelihoodColorLevel[],
        notAvailable: string
    ): RiskRegisterMatrixCell {
        return markLenientSource({
            ...raw,
            agentId: raw['agentId'] as string,
            agentName: (raw['agentName'] as string) ?? '',
            consequenceRating: raw['consequenceRating'] != null ? String(raw['consequenceRating']) : null,
            color: (raw['color'] as string) ?? '',
            // Resolve the DASH-2567 config-driven display values so the templatized PDF matches the
            // on-screen table: Likelihood -> level Name, Exposure Band -> QuantitativeName, Method of
            // assessment -> title. Other statistic keys stay as their raw register values.
            methodOfAssessment: getRegisterStatisticValue(raw, 'methodOfAssessment', exposureLikelihoodLevels, notAvailable),
            likelihood: getRegisterStatisticValue(raw, 'likelihood', exposureLikelihoodLevels, notAvailable),
            exposureBand: getRegisterStatisticValue(raw, 'exposureBand', exposureLikelihoodLevels, notAvailable)
        });
    }

    getDefaultUserInputs(): Record<string, string> {
        return { notes: '' };
    }

    getEmailTemplate(_entity: RiskRegisterReportEntity): EmailTemplate {
        return {
            subject: 'Risk Register Report',
            message: 'Please find attached the risk register report.',
            ccEmails: []
        };
    }

    getEntityFieldSources(): EntityFieldSource[] {
        return [
            {
                key: 'riskAssessment',
                label: 'Risk Assessment',
                accent: 'var(--primary)',
                kind: FieldSourceKind.Inline,
                fields: [
                    { path: 'riskAssessment.name', label: 'Assessment Name' }
                ]
            }
        ];
    }
}
